import { useEffect, useState } from "react";
import AdminAuthGuard from "../components/admin/AdminAuthGuard";
import AdminLayout from "../components/admin/AdminLayout";
import KpiTile from "../components/admin/KpiTile";
import FunnelBar from "../components/admin/FunnelBar";
import RecentAuditsList from "../components/admin/RecentAuditsList";
import RecentPaymentsList from "../components/admin/RecentPaymentsList";
import RecentImplList from "../components/admin/RecentImplList";
import { getAdminOverview } from "../lib/api/index";
import { logger } from "../lib/logger";

function Inner({ admin }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getAdminOverview()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => {
        logger.error("admin_overview_failed", { status: e?.response?.status });
        if (!cancelled) setErr("Could not load dashboard. Refresh.");
      });
    return () => { cancelled = true; };
  }, []);

  if (err) {
    return (
      <AdminLayout admin={admin}>
        <div className="sf-card" data-testid="admin-overview-error">{err}</div>
      </AdminLayout>
    );
  }
  if (!data) {
    return (
      <AdminLayout admin={admin}>
        <div className="sf-muted-text" data-testid="admin-overview-loading">Loading overview…</div>
      </AdminLayout>
    );
  }

  const k = data.kpis;
  const f = data.funnel;
  const maxFunnel = Math.max(...Object.values(f), 1);
  const r = data.rates;
  const recent = data.recent;

  return (
    <AdminLayout admin={admin}>
      <div data-testid="admin-overview-page">
        <div className="sf-eyebrow" style={{ marginBottom: 8 }}>Dashboard</div>
        <h1 className="sf-display" style={{ fontSize: 28, marginBottom: 22 }} data-testid="admin-overview-headline">
          Operating overview.
        </h1>

        {/* 9 KPI tiles */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 28,
        }} data-testid="admin-kpi-grid">
          <KpiTile testid="kpi-audit-starts" label="Audit starts" value={k.audit_starts} sub="distinct sessions" />
          <KpiTile testid="kpi-completions" label="Completions" value={k.audit_completions} sub={`${(k.completion_rate * 100).toFixed(1)}% rate`} />
          <KpiTile testid="kpi-preview-views" label="Preview views" value={k.preview_views} sub="distinct sessions" />
          <KpiTile testid="kpi-paid-audits" label="Paid audits" value={k.paid_audits} sub="audit_sessions.paid=true" />
          <KpiTile testid="kpi-revenue" label="Revenue" value={`$${k.revenue_usd.toLocaleString()}`} sub="paid × $97" />
          <KpiTile testid="kpi-impl-total" label="Impl requests" value={k.implementation_requests} sub={`${k.open_implementation_requests} open`} />
          <KpiTile testid="kpi-pdf-downloads" label="PDF downloads" value={k.pdf_downloads} sub="distinct sessions" />
          <KpiTile testid="kpi-unlock-to-pay" label="Unlock → Pay" value={`${(r.rate_checkout_start_to_complete * 100).toFixed(1)}%`} sub="checkout success" />
          <KpiTile testid="kpi-preview-to-unlock" label="Preview → Unlock" value={`${(r.rate_preview_to_unlock_click * 100).toFixed(1)}%`} sub="unlock-click rate" />
        </div>

        {/* Funnel */}
        <div className="sf-card" style={{ marginBottom: 28 }} data-testid="admin-funnel">
          <div className="sf-eyebrow" style={{ marginBottom: 12 }}>Funnel (distinct sessions)</div>
          <FunnelBar testid="funnel-step-1"  label="Audit start"               value={f.audit_starts}                   max={maxFunnel} />
          <FunnelBar testid="funnel-step-2"  label="Audit complete"            value={f.audit_completions}              max={maxFunnel} />
          <FunnelBar testid="funnel-step-3"  label="Preview view"              value={f.preview_views}                  max={maxFunnel} />
          <FunnelBar testid="funnel-step-4"  label="Unlock click"              value={f.unlock_clicks}                  max={maxFunnel} />
          <FunnelBar testid="funnel-step-5"  label="Checkout started"          value={f.checkout_starteds}              max={maxFunnel} />
          <FunnelBar testid="funnel-step-6"  label="Checkout completed"        value={f.checkout_completeds}            max={maxFunnel} />
          <FunnelBar testid="funnel-step-7"  label="Report view"               value={f.report_views}                   max={maxFunnel} />
          <FunnelBar testid="funnel-step-8"  label="PDF downloaded"            value={f.pdf_downloadeds}                max={maxFunnel} />
          <FunnelBar testid="funnel-step-9"  label="Implementation request"   value={f.implementation_request_submits} max={maxFunnel} />
        </div>

        {/* Recent panels */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
          <RecentAuditsList recentAudits={recent.recent_audits} />
          <RecentPaymentsList recentPayments={recent.recent_payments} />
          <RecentImplList recentImplementationRequests={recent.recent_implementation_requests} />
        </div>
      </div>
    </AdminLayout>
  );
}

export default function AdminDashboard() {
  return <AdminAuthGuard>{(admin) => <Inner admin={admin} />}</AdminAuthGuard>;
}
